"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import Link from "@/lib/localized-link";
import { useT } from "@/lib/locale-context";
import { useHoldInterruptions } from "@/lib/floating-slot";

const STORAGE_KEY = "menzil-cookie-consent";
// The banner shows from the sm breakpoint up (hidden on phones since
// 2026-05-01, #171): only a banner on screen holds the other prompts.
const SHOWN_QUERY = "(min-width: 640px)";

function subscribeWidth(cb: () => void) {
  const mq = window.matchMedia(SHOWN_QUERY);
  mq.addEventListener("change", cb);
  return () => mq.removeEventListener("change", cb);
}

export default function CookieBanner() {
  const t = useT();
  const [visible, setVisible] = useState(false);
  const wide = useSyncExternalStore(subscribeWidth, () => window.matchMedia(SHOWN_QUERY).matches, () => false);
  // The cookie note comes first: until it is answered, nothing else
  // comes up, the welcome card and the team's reply included (the
  // interruption budget, lib/floating-slot).
  useHoldInterruptions(visible && wide, "cookie");

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!localStorage.getItem(STORAGE_KEY)) {
      // Defer a tick so SSR markup matches initial client render
      requestAnimationFrame(() => setVisible(true));
    }
  }, []);

  const choose = (decision: "accept" | "reject") => {
    try { localStorage.setItem(STORAGE_KEY, decision); } catch { /* ignore */ }
    // Hiding the banner ends its hold: a prompt waiting its turn may follow.
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div data-cookie-banner className="hidden sm:block fixed inset-x-0 z-50 px-4 pb-4 sm:pb-6 bottom-[calc(env(safe-area-inset-bottom)+4rem)] sm:bottom-0">
      <div className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-2xl shadow-lg p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5">
        <p className="text-xs text-gray-700 flex-1 leading-relaxed">
          {t("cookie.banner")}{" "}
          <Link href="/cookies" className="font-medium text-gray-900 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-900">
            {t("cookie.banner.learn")}
          </Link>
        </p>
        <div className="flex gap-2 shrink-0 self-end sm:self-center">
          <button
            onClick={() => choose("reject")}
            className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            {t("cookie.reject")}
          </button>
          <button
            onClick={() => choose("accept")}
            className="px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            {t("cookie.accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
